import React, { useEffect, useMemo, useState } from 'react';
import { DatePicker, Select, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import FilterBar, { FilterField } from '../Common/FilterBar';

const { RangePicker } = DatePicker;

// Filter keys sent to the dashboard endpoints (see apply_dashboard_filters).
const FILTER_KEYS = [
  'project', 'assignee', 'status_name', 'label', 'priority', 'task_type', 'completion', 'due',
  'due_from', 'due_to', 'created_from', 'created_to',
];
// Everything kept in the URL: the filters plus the search text.
export const OVERVIEW_FILTER_KEYS = ['search', ...FILTER_KEYS];

const PRIORITY_OPTIONS = [
  { value: '0', label: 'Low' },
  { value: '1', label: 'Medium' },
  { value: '2', label: 'High' },
  { value: '3', label: 'Urgent' },
];
const COMPLETION_OPTIONS = [
  { value: 'open', label: 'Incomplete' },
  { value: 'completed', label: 'Completed' },
];
const DUE_OPTIONS = [
  { value: 'overdue', label: 'Overdue' },
  { value: 'none', label: 'No due date' },
  { value: 'set', label: 'Has a due date' },
];
const EMPTY_OPTIONS = { projects: [], assignees: [], statuses: [], task_types: [], labels: [] };

const toOptions = (list) => (list || []).map((x) => ({ value: x.name, label: x.name }));
const labelOf = (opts, v) => (opts.find((o) => String(o.value) === String(v)) || {}).label || v;
const fmt = (d) => dayjs(d).format('MMM D, YYYY');
const rangeText = (from, to) => (from && to ? `${fmt(from)} – ${fmt(to)}` : from ? `from ${fmt(from)}` : `until ${fmt(to)}`);

// Project Board overview toolbar, same pattern as Invoices: search + refresh +
// a Filter button (modal on desktop, bottom sheet on phones) + removable chips.
// `value` is a plain object of filter keys → strings; `onChange` receives the next object.
const OverviewFilters = ({ value, onChange, onRefresh, refreshing }) => {
  // Every option comes only from projects I own or belong to (the chosen one, if any).
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [searchLive, setSearchLive] = useState(value.search || '');

  useEffect(() => { setSearchLive(value.search || ''); }, [value.search]);

  useEffect(() => {
    axios.get('/project_board/dashboard/filter_options/', { params: value.project ? { project: value.project } : {} })
      .then((r) => setOptions({ ...EMPTY_OPTIONS, ...(r.data || {}) }))
      .catch(() => setOptions(EMPTY_OPTIONS));
  }, [value.project]);

  const set = (patch) => {
    const next = { ...value, ...patch };
    Object.keys(next).forEach((k) => { if (next[k] === undefined || next[k] === null || next[k] === '') delete next[k]; });
    onChange(next);
  };

  const range = (from, to) => (value[from] || value[to])
    ? [value[from] ? dayjs(value[from]) : null, value[to] ? dayjs(value[to]) : null]
    : null;
  const setRange = (from, to) => (dates) => set({
    [from]: dates?.[0] ? dates[0].format('YYYY-MM-DD') : undefined,
    [to]: dates?.[1] ? dates[1].format('YYYY-MM-DD') : undefined,
  });

  const projectOptions = options.projects.map((p) => ({ value: p.id, label: p.project_code ? `${p.project_code} · ${p.name}` : p.name }));
  const assigneeOptions = options.assignees.map((a) => ({ value: a.id, label: a.name }));

  // Chips under the bar, one per applied filter, each removable on its own.
  const activeFilters = useMemo(() => {
    const chips = [];
    const add = (key, label, clear) => chips.push({ key, label, onRemove: () => set(clear) });
    if (value.project) add('project', `Project: ${labelOf(projectOptions, value.project)}`, { project: undefined, assignee: undefined, task_type: undefined, status_name: undefined, label: undefined });
    if (value.assignee) add('assignee', `Assignee: ${labelOf(assigneeOptions, value.assignee)}`, { assignee: undefined });
    if (value.status_name) add('status_name', `Status: ${value.status_name}`, { status_name: undefined });
    if (value.completion) add('completion', `Completion: ${labelOf(COMPLETION_OPTIONS, value.completion)}`, { completion: undefined });
    if (value.due) add('due', `Due: ${labelOf(DUE_OPTIONS, value.due)}`, { due: undefined });
    if (value.priority) add('priority', `Priority: ${labelOf(PRIORITY_OPTIONS, value.priority)}`, { priority: undefined });
    if (value.task_type) add('task_type', `Type: ${value.task_type}`, { task_type: undefined });
    if (value.label) add('label', `Label: ${value.label}`, { label: undefined });
    if (value.due_from || value.due_to) add('due_range', `Due date: ${rangeText(value.due_from, value.due_to)}`, { due_from: undefined, due_to: undefined });
    if (value.created_from || value.created_to) add('created_range', `Created: ${rangeText(value.created_from, value.created_to)}`, { created_from: undefined, created_to: undefined });
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options]);

  const clearFilters = () => onChange(value.search ? { search: value.search } : {});
  const selectProps = { allowClear: true, style: { width: '100%' }, placeholder: 'All' };

  return (
    <div style={{ marginBottom: 24 }}>
      <FilterBar
        searchValue={searchLive}
        onSearchChange={(text) => { setSearchLive(text); if (!text) set({ search: undefined }); }}
        onSearch={(text) => set({ search: (text || '').trim() || undefined })}
        searchPlaceholder="Search tasks by title or code"
        activeCount={activeFilters.length}
        activeFilters={activeFilters}
        onClear={clearFilters}
        title="Filter Overview"
        onRefresh={onRefresh}
        refreshing={refreshing}
      >
        <FilterField label="Project">
          <Select
            {...selectProps}
            showSearch
            optionFilterProp="label"
            value={value.project}
            onChange={(v) => set({ project: v, assignee: undefined, task_type: undefined, status_name: undefined, label: undefined })}
            options={projectOptions}
          />
        </FilterField>
        <FilterField
          label={(
            <span>
              Assignee{' '}
              <Tooltip title='Pick a team member to see their tasks in "Total Tasks", Recent Activity and the Schedule. The "My Tasks" card always shows your own.'>
                <InfoCircleOutlined style={{ fontWeight: 400 }} />
              </Tooltip>
            </span>
          )}
        >
          <Select
            {...selectProps}
            placeholder="Everyone"
            showSearch
            optionFilterProp="label"
            value={value.assignee}
            onChange={(v) => set({ assignee: v })}
            options={assigneeOptions}
          />
        </FilterField>
        <FilterField label="Status">
          <Select {...selectProps} showSearch value={value.status_name} onChange={(v) => set({ status_name: v })} options={toOptions(options.statuses)} />
        </FilterField>
        <FilterField label="Completion">
          <Select {...selectProps} value={value.completion} onChange={(v) => set({ completion: v })} options={COMPLETION_OPTIONS} />
        </FilterField>
        <FilterField label="Due">
          <Select {...selectProps} value={value.due} onChange={(v) => set({ due: v })} options={DUE_OPTIONS} />
        </FilterField>
        <FilterField label="Priority">
          <Select {...selectProps} value={value.priority} onChange={(v) => set({ priority: v })} options={PRIORITY_OPTIONS} />
        </FilterField>
        <FilterField label="Type">
          <Select {...selectProps} showSearch value={value.task_type} onChange={(v) => set({ task_type: v })} options={toOptions(options.task_types)} />
        </FilterField>
        <FilterField label="Label">
          <Select
            {...selectProps}
            showSearch
            value={value.label}
            onChange={(v) => set({ label: v })}
            options={toOptions(options.labels)}
            notFoundContent="No labels in your projects"
          />
        </FilterField>
        <FilterField label="Due date between">
          <RangePicker style={{ width: '100%' }} allowEmpty={[true, true]} value={range('due_from', 'due_to')} onChange={setRange('due_from', 'due_to')} />
        </FilterField>
        <FilterField label="Created between">
          <RangePicker style={{ width: '100%' }} allowEmpty={[true, true]} value={range('created_from', 'created_to')} onChange={setRange('created_from', 'created_to')} />
        </FilterField>
      </FilterBar>
    </div>
  );
};

export default OverviewFilters;
